import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  History,
  Info,
  LogOut,
  Mail,
  Pencil,
  RotateCw,
  ShieldCheck,
  UserCheck,
  UserX,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { DetailHeader } from '@/ui/DetailHeader';
import { EstadoDaTela } from '@/ui/EstadoDaTela';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';

import type {
  DetalheDoUsuario,
  EntradaDoHistoricoAdministrativo,
  EntradaDoHistoricoDeAcesso,
  ResultadoDoAcesso,
  SessaoAtiva,
} from './contrato';
import {
  useAcaoDaConta,
  useAcaoDoConvite,
  useDetalheDoUsuario,
  useHistoricoAdministrativo,
  useHistoricoDeAcessos,
  type AcaoDaConta,
} from './dados';
import { DialogoDeAcao } from './DialogoDeAcao';
import {
  ROTULO_DA_SITUACAO,
  ROTULO_DO_CONVITE,
  ROTULO_DO_METODO,
  TOM_DA_SITUACAO,
  VALIDADE_DO_LINK_EM_MINUTOS,
  avisoDeEntrega,
  data,
  dataEHora,
  rotuloDoSegundoFator,
  rotuloDosPerfis,
} from './rotulos';

const ABAS = ['dados', 'seguranca', 'acessos', 'administrativo'] as const;
type Aba = (typeof ABAS)[number];

type Dialogo =
  | 'link'
  | 'segundo-fator'
  | 'revogar'
  | 'bloquear'
  | 'desbloquear'
  | 'reenviar'
  | 'cancelar'
  | null;

/**
 * `/admin/usuarios/:id`: a tela interna de uma pessoa, em quatro abas — Dados,
 * Segurança, Histórico de acessos e Histórico administrativo. O `:id` é o
 * identificador da pessoa no provedor de identidade, o mesmo do convite ao
 * último acesso.
 *
 * O que aparece de ação é o que a API diz que pode (`acoes`): a própria conta,
 * conta de administrador para quem não é, e convite fora da alçada não trazem
 * botão nenhum (regra 7 de interface).
 */
export function UsuarioDetalhePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [parametros, setParametros] = useSearchParams();
  const aba: Aba = (ABAS as readonly string[]).includes(parametros.get('aba') ?? '')
    ? (parametros.get('aba') as Aba)
    : 'dados';
  const consulta = useDetalheDoUsuario(id);
  const [dialogo, setDialogo] = useState<Dialogo>(null);
  const acaoDaConta = useAcaoDaConta(id);
  const acaoDoConvite = useAcaoDoConvite();

  async function executar(acao: AcaoDaConta, corpo: Record<string, unknown>) {
    const r = await acaoDaConta.mutateAsync({ acao, corpo });
    toast.success(r.mensagem);
  }

  async function noConvite(acao: 'reenviar' | 'cancelar', corpo: Record<string, unknown>) {
    const conviteId = consulta.data?.convite?.id;
    if (!conviteId) return;
    const r = await acaoDoConvite.mutateAsync({ conviteId, acao, corpo });
    toast.success(r.mensagem);
    // Cancelado, o convite sai da lista de pessoas; reenviado, nasce identidade
    // nova, e a tela passa a ser a do convite novo.
    if (acao === 'cancelar') navigate('/admin/usuarios', { replace: true });
    else if (r.id !== id) navigate(`/admin/usuarios/${r.id}`, { replace: true });
  }

  return (
    <div className="min-w-0 space-y-4">
      <EstadoDaTela
        carregando={consulta.isPending}
        erro={consulta.error}
        aoTentarDeNovo={() => void consulta.refetch()}
        descricao="a pessoa"
      >
        {consulta.data && (
          <>
            <DetailHeader
              backTo="/admin/usuarios"
              backLabel="Usuários"
              title={consulta.data.nome}
              subtitle={rotuloDosPerfis(consulta.data.perfis)}
              status={{
                variant: TOM_DA_SITUACAO[consulta.data.situacao],
                label: ROTULO_DA_SITUACAO[consulta.data.situacao],
              }}
              actions={
                consulta.data.acoes.editar ? (
                  <Button asChild variant="outline">
                    <Link to={`/admin/usuarios/${id}/editar`}>
                      <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
                      Editar
                    </Link>
                  </Button>
                ) : undefined
              }
            />

            {consulta.data.restricao && (
              <Alert>
                <Info className="h-4 w-4" aria-hidden="true" />
                <AlertDescription>{consulta.data.restricao}</AlertDescription>
              </Alert>
            )}

            <Tabs
              value={aba}
              onValueChange={(v) => setParametros({ aba: v }, { replace: true })}
              className="min-w-0"
            >
              <TabsList className="flex h-auto w-full flex-wrap justify-start">
                <TabsTrigger value="dados">Dados</TabsTrigger>
                <TabsTrigger value="seguranca">Segurança</TabsTrigger>
                <TabsTrigger value="acessos">Histórico de acessos</TabsTrigger>
                <TabsTrigger value="administrativo">Histórico administrativo</TabsTrigger>
              </TabsList>

              <TabsContent value="dados" className="space-y-4">
                <AbaDados pessoa={consulta.data} aoAgir={setDialogo} />
              </TabsContent>
              <TabsContent value="seguranca" className="space-y-4">
                <AbaSeguranca pessoa={consulta.data} aoAgir={setDialogo} />
              </TabsContent>
              <TabsContent value="acessos">
                <AbaAcessos
                  id={id}
                  ativa={aba === 'acessos'}
                  desde={consulta.data.historicoDesde}
                />
              </TabsContent>
              <TabsContent value="administrativo">
                <AbaAdministrativo id={id} ativa={aba === 'administrativo'} />
              </TabsContent>
            </Tabs>

            <Dialogos
              pessoa={consulta.data}
              dialogo={dialogo}
              fechar={() => setDialogo(null)}
              executar={executar}
              noConvite={noConvite}
            />
          </>
        )}
      </EstadoDaTela>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Dados
// -----------------------------------------------------------------------------

function Campo({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-muted-foreground">{rotulo}</dt>
      <dd className="mt-0.5 break-words text-sm text-foreground">{children}</dd>
    </div>
  );
}

function AbaDados({ pessoa, aoAgir }: { pessoa: DetalheDoUsuario; aoAgir: (d: Dialogo) => void }) {
  const c = pessoa.convite;
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Dados</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2">
            <Campo rotulo="Nome">{pessoa.nome}</Campo>
            <Campo rotulo="E-mail">{pessoa.email || '—'}</Campo>
            <Campo rotulo="Perfil de acesso">{rotuloDosPerfis(pessoa.perfis)}</Campo>
            {pessoa.tenant !== 'TEG' && (
              <Campo
                rotulo={pessoa.tenant === 'DRE' ? 'Diretoria regional' : 'Empresa credenciada'}
              >
                {pessoa.recorte?.nome ?? 'Sem recorte: a pessoa não vê dado nenhum até ter um'}
              </Campo>
            )}
            <Campo rotulo="Criado em">{dataEHora(pessoa.criadoEm)}</Campo>
            <Campo rotulo="Criado por">
              {pessoa.criadoPor ?? 'Conta criada no credenciamento ou na primeira entrada'}
            </Campo>
            <Campo rotulo="Último acesso">
              {pessoa.ultimoAcesso ? dataEHora(pessoa.ultimoAcesso) : 'Nunca entrou'}
            </Campo>
          </dl>
        </CardContent>
      </Card>

      {c && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Convite</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {avisoDeEntrega(pessoa.entregaDoEmail, pessoa.email) && (
              <Alert>
                <Info className="h-4 w-4" aria-hidden="true" />
                <AlertDescription>
                  {avisoDeEntrega(pessoa.entregaDoEmail, pessoa.email)} O convite só chega à pessoa
                  onde o envio de e-mail estiver ligado.
                </AlertDescription>
              </Alert>
            )}
            {c.limpezaPendente && (
              <Alert>
                <Info className="h-4 w-4" aria-hidden="true" />
                <AlertDescription>
                  O convite foi cancelado, mas a conta dele no provedor de identidade não foi
                  removida. Use "Cancelar e limpar" para terminar.
                </AlertDescription>
              </Alert>
            )}
            <dl className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Situação do convite">
                <Badge variant="outline">{ROTULO_DO_CONVITE[c.situacao]}</Badge>
              </Campo>
              <Campo rotulo="Enviado em">{dataEHora(c.enviadoEm)}</Campo>
              <Campo rotulo="Enviado por">{c.enviadoPor ?? '—'}</Campo>
              <Campo rotulo={c.situacao === 'EXPIRADO' ? 'Expirou em' : 'Expira em'}>
                {dataEHora(c.expiraEm)}
              </Campo>
              {c.aceitoEm && <Campo rotulo="Aceito em">{dataEHora(c.aceitoEm)}</Campo>}
              {c.canceladoEm && <Campo rotulo="Cancelado em">{dataEHora(c.canceladoEm)}</Campo>}
            </dl>
            {(pessoa.acoes.reenviarConvite || pessoa.acoes.cancelarConvite) && (
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                {pessoa.acoes.reenviarConvite && (
                  <Button
                    onClick={() => aoAgir('reenviar')}
                    disabled={c.enviosHoje >= c.limiteDeEnvios}
                  >
                    <RotateCw className="mr-2 h-4 w-4" aria-hidden="true" />
                    {c.situacao === 'FALHA' ? 'Reemitir convite' : 'Reenviar convite'}
                  </Button>
                )}
                {pessoa.acoes.cancelarConvite && (
                  <Button variant="outline" onClick={() => aoAgir('cancelar')}>
                    <XCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                    {c.situacao === 'FALHA' || c.limpezaPendente
                      ? 'Cancelar e limpar'
                      : 'Cancelar convite'}
                  </Button>
                )}
                {pessoa.acoes.reenviarConvite && (
                  <p className="text-xs text-muted-foreground">
                    {c.enviosHoje >= c.limiteDeEnvios
                      ? `Limite de ${c.limiteDeEnvios} convites para este e-mail em 24 horas atingido.`
                      : `Convites para este e-mail nas últimas 24 horas: ${c.enviosHoje} de ${c.limiteDeEnvios}.`}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Segurança
// -----------------------------------------------------------------------------

const colunasDeSessao: DataTableColumn<SessaoAtiva>[] = [
  { id: 'inicio', header: 'Aberta em', cell: (s) => dataEHora(s.iniciadaEm) },
  { id: 'atividade', header: 'Última atividade', cell: (s) => dataEHora(s.ultimaAtividadeEm) },
  { id: 'ip', header: 'Origem (IP)', cell: (s) => s.ipMascarado ?? '—' },
  { id: 'navegador', header: 'Navegador', cell: (s) => s.navegador ?? '—' },
  {
    id: 'fator',
    header: 'Segundo fator',
    cell: (s) => (s.segundoFatorComprovado ? 'Comprovado' : 'Não comprovado'),
  },
];

function AbaSeguranca({
  pessoa,
  aoAgir,
}: {
  pessoa: DetalheDoUsuario;
  aoAgir: (d: Dialogo) => void;
}) {
  const a = pessoa.acoes;
  const fator = pessoa.seguranca.segundoFator;
  const algumaAcao =
    a.enviarLinkSenha || a.exigirSegundoFator || a.revogarSessoes || a.bloquear || a.desbloquear;
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Segundo fator</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2">
            <Campo rotulo="Situação">
              {fator === 'aplicativo' || fator === 'email'
                ? 'Habilitado'
                : rotuloDoSegundoFator(fator)}
            </Campo>
            {(fator === 'aplicativo' || fator === 'email') && (
              <Campo rotulo="Tipo">{ROTULO_DO_METODO[fator]}</Campo>
            )}
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sessões ativas</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={colunasDeSessao}
            data={pessoa.seguranca.sessoesAtivas}
            ariaLabel="Sessões ativas"
            emptyIcon={LogOut}
            emptyTitle="Nenhuma sessão aberta"
            emptyDescription="A pessoa não está dentro do sistema agora."
            showPagination={false}
          />
        </CardContent>
      </Card>

      {algumaAcao && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Ações</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {a.enviarLinkSenha && (
              <div className="space-y-1">
                <Button onClick={() => aoAgir('link')}>
                  <Mail className="mr-2 h-4 w-4" aria-hidden="true" />
                  Enviar link para trocar a senha
                </Button>
                <p className="text-xs text-muted-foreground">
                  A própria pessoa cria a senha nova pelo link, que vai para {pessoa.email} e vale{' '}
                  {VALIDADE_DO_LINK_EM_MINUTOS} minutos.
                </p>
                {avisoDeEntrega(pessoa.entregaDoEmail, pessoa.email) && (
                  <p className="text-xs text-muted-foreground">
                    {avisoDeEntrega(pessoa.entregaDoEmail, pessoa.email)}
                  </p>
                )}
              </div>
            )}
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              {a.exigirSegundoFator && (
                <Button variant="outline" onClick={() => aoAgir('segundo-fator')}>
                  <ShieldCheck className="mr-2 h-4 w-4" aria-hidden="true" />
                  Exigir novo cadastro do segundo fator
                </Button>
              )}
              {a.revogarSessoes && (
                <Button variant="outline" onClick={() => aoAgir('revogar')}>
                  <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                  Revogar sessões
                </Button>
              )}
              {a.bloquear && (
                <Button variant="outline" onClick={() => aoAgir('bloquear')}>
                  <UserX className="mr-2 h-4 w-4" aria-hidden="true" />
                  Bloquear
                </Button>
              )}
              {a.desbloquear && (
                <Button variant="outline" onClick={() => aoAgir('desbloquear')}>
                  <UserCheck className="mr-2 h-4 w-4" aria-hidden="true" />
                  Desbloquear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Históricos
// -----------------------------------------------------------------------------

const ROTULO_DO_RESULTADO: Record<ResultadoDoAcesso, string> = {
  SUCESSO: 'Sucesso',
  FALHA: 'Falha',
  INFORMACAO: '—',
};

const colunasDeAcesso: DataTableColumn<EntradaDoHistoricoDeAcesso>[] = [
  { id: 'quando', header: 'Data e hora', cell: (e) => dataEHora(e.quando) },
  { id: 'evento', header: 'Evento', cell: (e) => e.descricao },
  {
    id: 'resultado',
    header: 'Resultado',
    cell: (e) =>
      e.resultado === 'FALHA' ? (
        <Badge variant="destructive">{ROTULO_DO_RESULTADO.FALHA}</Badge>
      ) : (
        ROTULO_DO_RESULTADO[e.resultado]
      ),
  },
  { id: 'ip', header: 'Origem (IP)', cell: (e) => e.ipMascarado ?? '—' },
  {
    id: 'fator',
    header: 'Segundo fator',
    cell: (e) =>
      e.segundoFator === 'aplicativo' || e.segundoFator === 'email'
        ? ROTULO_DO_METODO[e.segundoFator]
        : e.segundoFator === 'comprovado'
          ? 'Comprovado'
          : '—',
  },
];

function AbaAcessos({ id, ativa, desde }: { id: string; ativa: boolean; desde: string | null }) {
  const [pagina, setPagina] = useState(1);
  const consulta = useHistoricoDeAcessos(id, pagina, ativa);
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {desde
          ? `Entradas, falhas, saídas e sessões. Há registro de acesso neste ambiente desde ${data(desde)}.`
          : 'Entradas, falhas, saídas e sessões. Ainda não há registro de acesso neste ambiente.'}
      </p>
      <EstadoDaTela
        carregando={consulta.isPending}
        erro={consulta.error}
        aoTentarDeNovo={() => void consulta.refetch()}
        descricao="o histórico de acessos"
        esqueleto={{ colunas: 5 }}
      >
        <DataTable
          columns={colunasDeAcesso}
          data={consulta.data?.itens ?? []}
          isLoading={consulta.isPlaceholderData}
          total={consulta.data?.total ?? 0}
          page={pagina}
          perPage={20}
          onPageChange={setPagina}
          ariaLabel="Histórico de acessos"
          emptyIcon={History}
          emptyTitle="Nenhum acesso registrado"
          emptyDescription="Quando a pessoa entrar, as entradas e as sessões aparecem aqui."
        />
      </EstadoDaTela>
    </div>
  );
}

const colunasAdministrativas: DataTableColumn<EntradaDoHistoricoAdministrativo>[] = [
  { id: 'quando', header: 'Data e hora', cell: (e) => dataEHora(e.quando) },
  { id: 'acao', header: 'O que foi feito', cell: (e) => e.descricao },
  { id: 'autor', header: 'Quem fez', cell: (e) => e.autor ?? 'A própria pessoa' },
  { id: 'motivo', header: 'Motivo', cell: (e) => e.motivo ?? '—' },
];

function AbaAdministrativo({ id, ativa }: { id: string; ativa: boolean }) {
  const [pagina, setPagina] = useState(1);
  const consulta = useHistoricoAdministrativo(id, pagina, ativa);
  return (
    <EstadoDaTela
      carregando={consulta.isPending}
      erro={consulta.error}
      aoTentarDeNovo={() => void consulta.refetch()}
      descricao="o histórico administrativo"
      esqueleto={{ colunas: 4 }}
    >
      <DataTable
        columns={colunasAdministrativas}
        data={consulta.data?.itens ?? []}
        isLoading={consulta.isPlaceholderData}
        total={consulta.data?.total ?? 0}
        page={pagina}
        perPage={20}
        onPageChange={setPagina}
        ariaLabel="Histórico administrativo"
        emptyIcon={History}
        emptyTitle="Nenhuma ação administrativa"
        emptyDescription="Convite, mudança de perfil, bloqueio e as demais ações aparecem aqui, com quem fez."
      />
    </EstadoDaTela>
  );
}

// -----------------------------------------------------------------------------
// Diálogos das ações
// -----------------------------------------------------------------------------

function Dialogos({
  pessoa,
  dialogo,
  fechar,
  executar,
  noConvite,
}: {
  pessoa: DetalheDoUsuario;
  dialogo: Dialogo;
  fechar: () => void;
  executar: (acao: AcaoDaConta, corpo: Record<string, unknown>) => Promise<void>;
  noConvite: (acao: 'reenviar' | 'cancelar', corpo: Record<string, unknown>) => Promise<void>;
}) {
  return (
    <>
      <DialogoDeAcao
        aberto={dialogo === 'link'}
        aoFechar={fechar}
        titulo="Enviar link para trocar a senha"
        descricao={`O link vai para ${pessoa.email}, vale ${VALIDADE_DO_LINK_EM_MINUTOS} minutos e serve uma vez só. A própria pessoa cria a senha nova; as sessões continuam até a troca.`}
        rotuloDoBotao="Enviar link"
        pedeMotivo={false}
        aviso={
          pessoa.entregaDoEmail === 'caixa-de-testes'
            ? avisoDeEntrega(pessoa.entregaDoEmail, pessoa.email)
            : null
        }
        bloqueio={
          pessoa.entregaDoEmail === 'retido'
            ? `${avisoDeEntrega('retido', pessoa.email)} O link só pode ser enviado onde o envio estiver ligado.`
            : null
        }
        aoConfirmar={({ chaveIdempotencia }) => executar('link-troca-senha', { chaveIdempotencia })}
      />
      <DialogoDeAcao
        aberto={dialogo === 'segundo-fator'}
        aoFechar={fechar}
        titulo="Exigir novo cadastro do segundo fator"
        descricao="O segundo fator cadastrado é apagado e as sessões abertas caem. Na próxima entrada, a pessoa cadastra o segundo fator de novo."
        rotuloDoBotao="Exigir novo cadastro"
        perigosa
        aoConfirmar={(d) => executar('exigir-segundo-fator', d)}
      />
      <DialogoDeAcao
        aberto={dialogo === 'revogar'}
        aoFechar={fechar}
        titulo="Revogar sessões"
        descricao="Todas as sessões abertas desta pessoa são encerradas. Ela continua podendo entrar de novo."
        rotuloDoBotao="Revogar sessões"
        aoConfirmar={(d) => executar('revogar-sessoes', d)}
      />
      <DialogoDeAcao
        aberto={dialogo === 'bloquear'}
        aoFechar={fechar}
        titulo="Bloquear conta"
        descricao="A pessoa sai de todas as sessões e não consegue entrar até a conta ser desbloqueada."
        rotuloDoBotao="Bloquear"
        perigosa
        aoConfirmar={(d) => executar('bloquear', d)}
      />
      <DialogoDeAcao
        aberto={dialogo === 'desbloquear'}
        aoFechar={fechar}
        titulo="Desbloquear conta"
        descricao="A pessoa volta a poder entrar no sistema."
        rotuloDoBotao="Desbloquear"
        aoConfirmar={(d) => executar('desbloquear', d)}
      />
      <DialogoDeAcao
        aberto={dialogo === 'reenviar'}
        aoFechar={fechar}
        titulo={pessoa.convite?.situacao === 'FALHA' ? 'Reemitir convite' : 'Reenviar convite'}
        descricao={`Um convite novo vai para ${pessoa.email}, com prazo de 48 horas. O link do convite anterior deixa de valer.`}
        rotuloDoBotao="Reenviar"
        pedeMotivo={false}
        aviso={
          pessoa.entregaDoEmail === 'caixa-de-testes'
            ? avisoDeEntrega(pessoa.entregaDoEmail, pessoa.email)
            : null
        }
        bloqueio={
          pessoa.entregaDoEmail === 'retido'
            ? `${avisoDeEntrega('retido', pessoa.email)} O convite só pode ser enviado onde o envio estiver ligado.`
            : null
        }
        aoConfirmar={({ chaveIdempotencia }) => noConvite('reenviar', { chaveIdempotencia })}
      />
      <DialogoDeAcao
        aberto={dialogo === 'cancelar'}
        aoFechar={fechar}
        titulo={
          pessoa.convite?.situacao === 'FALHA' || pessoa.convite?.limpezaPendente
            ? 'Cancelar e limpar'
            : 'Cancelar convite'
        }
        descricao="O link enviado deixa de valer, a conta do convite no provedor de identidade é removida e a pessoa sai da lista. Conta já em uso nunca é removida por aqui."
        rotuloDoBotao="Cancelar convite"
        perigosa
        aoConfirmar={(d) => noConvite('cancelar', d)}
      />
    </>
  );
}

export default UsuarioDetalhePage;
